import { useEffect, useMemo, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import {
  downloadDocument,
  exportIndex,
  previewDocument,
  useChecklist,
  useCreateFolder,
  useDeleteDocument,
  useDeleteFolder,
  useDocument,
  useDocuments,
  useFolders,
  useMoveDocument,
  useMoveFolder,
  useRenameDocument,
  useRenameFolder,
  useSetDocumentChecklist,
  useUpload,
  useUploadZip,
} from '../api/rooms'
import type {
  DocumentResponse,
  FolderResponse,
  RoomResponse,
  RoomStatus,
  UploadResponse,
} from '../api/types'
import { ChecklistPicker } from '../components/ChecklistPicker'
import { checklistItemName, checklistStatusLabel } from '../lib/checklist'
import { FolderTree } from '../components/FolderTree'
import { UploadDropzone } from '../components/UploadDropzone'
import { DataPolicyGate } from '../components/DataPolicyGate'
import { dataPolicyBlocks } from '../lib/dataPolicy'
import { MoveTo } from '../components/MoveTo'
import { SearchResults } from '../components/SearchResults'
import { errorMessage, formatBytes, formatUtc } from '../lib/format'

/** The room's documents: tree on the left, the selected folder's documents on the right, a document's versions below. */
export function Room() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null)
  const [preview, setPreview] = useState<PreviewTarget | null>(null)
  const [query, setQuery] = useState('')
  const searching = query.trim().length >= 2
  // The request follows the typist, not the keystroke: every search is a read against the
  // per-user budget (60 a minute) and a full-text query, so a word typed letter by letter
  // costs one call, not six.
  const [settledQuery, setSettledQuery] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setSettledQuery(query.trim()), 300)
    return () => clearTimeout(t)
  }, [query])

  const folders = useFolders(roomId)
  const documents = useDocuments(roomId, selectedFolder)

  const folder = useMemo(
    () => folders.data?.find((f) => f.id === selectedFolder) ?? null,
    [folders.data, selectedFolder],
  )
  // Rights at the selected node come from the server: the folder's bits, or the room's at the root.
  // The coarse realm role caps them: the API's contributor gate refuses writes from a reviewer
  // whatever an owner granted, so a write control is shown only when both agree.
  const { me } = useAuth()
  const canContribute = ['contributor', 'room-owner', 'platform-admin'].some((r) =>
    me?.roles.includes(r),
  )
  const bits = folder?.permissions ?? room.permissions
  const here = canContribute ? bits : { ...bits, upload: false, editMetadata: false, delete: false }

  return (
    <div className="room-grid">
      <aside className="panel room-side">
        {folders.isPending && <p className="muted small">Loading folders…</p>}
        {folders.data && (
          <FolderTree
            folders={folders.data}
            selected={selectedFolder}
            onSelect={(id) => {
              setSelectedFolder(id)
              setSelectedDocument(null)
            }}
          />
        )}
        {here.upload && <NewFolder roomId={roomId} parentId={selectedFolder} />}
      </aside>

      <section className="stack room-main">
        <form className="search-box" role="search" onSubmit={(e) => e.preventDefault()}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search this room's documents"
            aria-label="Search this room's documents"
            maxLength={200}
          />
          {query && (
            <button type="button" className="btn btn-sm" onClick={() => setQuery('')}>
              Clear
            </button>
          )}
          <IndexExport roomId={roomId} />
        </form>

        {searching ? (
          <SearchResults
            roomId={roomId}
            query={settledQuery}
            onPreview={(documentId, name) => setPreview({ documentId, name })}
            onOpenFolder={(folderId) => {
              setSelectedFolder(folderId)
              setSelectedDocument(null)
              setQuery('')
            }}
          />
        ) : (
          <>
            <FolderHeading
              roomId={roomId}
              folder={folder}
              folders={folders.data ?? []}
              canEdit={here.editMetadata}
              canDelete={here.delete}
              onDeleted={() => setSelectedFolder(null)}
            />

            {/* ADR 0008: someone who may upload here but has not accepted the organization's data
                policy sees the policy where the upload control would be. The API refuses the upload
                either way; this is the way through, not the lock. */}
            {here.upload && dataPolicyBlocks(room) ? (
              <DataPolicyGate room={room} doing="you upload to this room" />
            ) : (
              <Uploads
                roomId={roomId}
                folderId={selectedFolder}
                canUpload={here.upload}
                roomStatus={room.status}
              />
            )}

            {documents.isPending && <p className="muted small">Loading documents…</p>}
            {documents.isError && (
              <div className="alert alert-error" role="alert">
                <p>{errorMessage(documents.error)}</p>
              </div>
            )}
            {documents.data && (
              <DocumentList
                roomId={roomId}
                documents={documents.data}
                selected={selectedDocument}
                onSelect={setSelectedDocument}
                onPreview={(documentId, name) => setPreview({ documentId, name })}
              />
            )}
          </>
        )}

        {preview && (
          <PreviewPane roomId={roomId} target={preview} onClose={() => setPreview(null)} />
        )}

        {selectedDocument && (
          <DocumentPanel
            roomId={roomId}
            documentId={selectedDocument}
            folders={folders.data ?? []}
            roomStatus={room.status}
            onClose={() => setSelectedDocument(null)}
            onPreview={(version, name) =>
              setPreview({ documentId: selectedDocument, name, version })
            }
          />
        )}
      </section>
    </div>
  )
}

interface PreviewTarget {
  documentId: string
  name: string
  version?: number
}

/**
 * The rendition in an iframe on an object URL. The bytes are fetched with the bearer
 * token (a plain iframe src would carry no token) and revoked when the pane closes.
 * What the reader may do with it is the PDF's own permission flags, set by the server.
 */
function PreviewPane({
  roomId,
  target,
  onClose,
}: {
  roomId: string
  target: PreviewTarget
  onClose: () => void
}) {
  const [state, setState] = useState<
    { url: string; watermarked: boolean } | { error: string } | null
  >(null)

  useEffect(() => {
    let url: string | null = null
    let cancelled = false
    setState(null)
    previewDocument(roomId, target.documentId, target.version)
      .then((r) => {
        if (cancelled) {
          URL.revokeObjectURL(r.url)
          return
        }
        url = r.url
        setState({ url: r.url, watermarked: r.watermarked })
      })
      .catch((e: unknown) => {
        if (!cancelled) setState({ error: errorMessage(e) })
      })
    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [roomId, target.documentId, target.version])

  return (
    <div className="panel preview-pane">
      <div className="row-between">
        <h2 className="h2 no-margin">
          {target.name}
          {target.version ? <span className="small muted"> version {target.version}</span> : null}
          {state && 'watermarked' in state && state.watermarked && (
            <span className="chip"> watermarked</span>
          )}
        </h2>
        <button type="button" className="btn" onClick={onClose}>
          Close preview
        </button>
      </div>
      {state === null && <p className="muted small">Rendering…</p>}
      {state && 'error' in state && <p className="error-text small">{state.error}</p>}
      {state && 'url' in state && (
        <iframe className="preview-frame" src={state.url} title={`Preview of ${target.name}`} />
      )}
    </div>
  )
}

/** The folder index as a file. The server lists what this reader may see and audits the copy. */
function IndexExport({ roomId }: { roomId: string }) {
  const [error, setError] = useState<string | null>(null)
  const run = (format: 'csv' | 'pdf') => {
    setError(null)
    exportIndex(roomId, format).catch((e: unknown) => setError(errorMessage(e)))
  }
  return (
    <span className="row-actions index-export">
      <span className="muted small">Index</span>
      <button type="button" className="btn btn-sm" onClick={() => run('csv')}>
        CSV
      </button>
      <button type="button" className="btn btn-sm" onClick={() => run('pdf')}>
        PDF
      </button>
      {error && (
        <span className="error small" role="alert">
          {error}
        </span>
      )}
    </span>
  )
}

function FolderHeading({
  roomId,
  folder,
  folders,
  canEdit,
  canDelete,
  onDeleted,
}: {
  roomId: string
  folder: FolderResponse | null
  folders: FolderResponse[]
  canEdit: boolean
  canDelete: boolean
  onDeleted: () => void
}) {
  const rename = useRenameFolder(roomId)
  const remove = useDeleteFolder(roomId)
  const move = useMoveFolder(roomId)

  return (
    <div className="row-between">
      <h2 className="h2 no-margin">
        {folder ? (
          <>
            <span className="mono muted">{folder.indexNumber}</span> {folder.name}
          </>
        ) : (
          'Room root'
        )}
      </h2>
      {folder && (
        <div className="row-actions">
          {canEdit && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                const name = window.prompt('New folder name', folder.name)
                if (name && name !== folder.name) rename.mutate({ folderId: folder.id, name })
              }}
            >
              Rename
            </button>
          )}
          {canEdit && (
            <MoveTo
              folders={folders}
              currentId={folder.parentId}
              excludeSubtreeOf={folder.id}
              onMove={(parentId) => move.mutate({ folderId: folder.id, parentId })}
              pending={move.isPending}
              error={move.error}
            />
          )}
          {canDelete && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (window.confirm(`Delete "${folder.name}" and everything in it?`))
                  remove.mutate(folder.id, { onSuccess: onDeleted })
              }}
            >
              Delete
            </button>
          )}
          {(rename.isError || remove.isError) && (
            <span className="error-text small">{errorMessage(rename.error ?? remove.error)}</span>
          )}
        </div>
      )}
    </div>
  )
}

function NewFolder({ roomId, parentId }: { roomId: string; parentId: string | null }) {
  const create = useCreateFolder(roomId)
  const [name, setName] = useState('')
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        const trimmed = name.trim()
        if (!trimmed) return
        create.mutate({ parentId, name: trimmed }, { onSuccess: () => setName('') })
      }}
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={parentId ? 'New subfolder' : 'New top-level folder'}
        aria-label="New folder name"
      />
      <button type="submit" className="btn" disabled={create.isPending || !name.trim()}>
        Add
      </button>
      {create.isError && <span className="error-text small">{errorMessage(create.error)}</span>}
    </form>
  )
}

function Uploads({
  roomId,
  folderId,
  canUpload,
  roomStatus,
}: {
  roomId: string
  folderId: string | null
  canUpload: boolean
  roomStatus: RoomStatus
}) {
  const upload = useUpload(roomId)
  const uploadZip = useUploadZip(roomId)
  // The checklist items the next files answer (ADR 0006); applied to every file of a
  // batch, and to every file inside a zip. Kept after a batch so a second drop for the
  // same item needs no re-picking.
  const checklist = useChecklist(roomId)
  const [itemIds, setItemIds] = useState<string[]>([])
  const [log, setLog] = useState<{ name: string; result: string; ok: boolean }[]>([])
  const busy = upload.isPending || uploadZip.isPending
  const canTag = canUpload && (roomStatus === 'Draft' || roomStatus === 'Open')

  const onFiles = async (files: File[]) => {
    for (const file of files) {
      try {
        if (file.name.toLowerCase().endsWith('.zip')) {
          const r = await uploadZip.mutateAsync({ folderId, file, itemIds })
          setLog((l) => [
            {
              name: file.name,
              ok: true,
              result: `${r.files.length} file(s), ${r.foldersCreated} folder(s)${r.skipped.length ? `, ${r.skipped.length} skipped` : ''}`,
            },
            ...l,
          ])
        } else {
          const r: UploadResponse = await upload.mutateAsync({ folderId, file, itemIds })
          setLog((l) => [
            { name: file.name, ok: r.scanStatus === 'Clean', result: describe(r) },
            ...l,
          ])
        }
      } catch (error) {
        setLog((l) => [{ name: file.name, ok: false, result: errorMessage(error) }, ...l])
      }
    }
  }

  return (
    <div className="stack-sm">
      {canTag && checklist.data && (
        <ChecklistPicker
          items={checklist.data.items}
          sections={checklist.data.sections}
          selected={itemIds}
          onChange={setItemIds}
          legend="The next files answer these checklist items"
        />
      )}
      <UploadDropzone disabled={!canUpload} busy={busy} onFiles={(files) => void onFiles(files)} />
      {log.length > 0 && (
        <ul className="upload-log" aria-label="Upload results">
          {log.slice(0, 8).map((entry, i) => (
            <li key={`${entry.name}-${i}`} className={entry.ok ? 'upload-ok' : 'upload-bad'}>
              <span className="upload-name">{entry.name}</span>
              <span className="small">{entry.result}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function describe(r: UploadResponse): string {
  switch (r.scanStatus) {
    case 'Clean':
      return `version ${r.versionNumber}, ${formatBytes(r.sizeBytes)}${r.isCurrent ? '' : ' (an older version stays current)'}`
    case 'Infected':
      return 'refused by the virus scanner; the file is quarantined'
    case 'Failed':
      return `not scanned: ${r.scanDetail ?? 'no verdict'}; the file stays in quarantine`
    default:
      return 'scanning…'
  }
}

export function DocumentList({
  roomId,
  documents,
  selected,
  onSelect,
  onPreview,
  folders,
}: {
  roomId: string
  documents: DocumentResponse[]
  selected: string | null
  onSelect: (id: string) => void
  onPreview?: (documentId: string, name: string) => void
  /** When given, a folder column names where each document sits (the checklist's room-wide view). */
  folders?: FolderResponse[]
}) {
  if (documents.length === 0) return <p className="muted small">No documents here.</p>
  const folderName = (id: string | null) => {
    if (!id) return 'Room root'
    const f = folders?.find((x) => x.id === id)
    return f ? `${f.indexNumber} ${f.name}` : '…'
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Name</th>
          {folders && <th>Folder</th>}
          <th>Version</th>
          <th>Size</th>
          <th>Uploaded</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {documents.map((d) => (
          <tr key={d.id} className={selected === d.id ? 'row-selected' : undefined}>
            <td>
              <button type="button" className="link-btn" onClick={() => onSelect(d.id)}>
                {d.name}
              </button>
              {d.pendingVersions > 0 && <span className="chip chip-warn">scanning</span>}
              {d.refusedVersions > 0 && <span className="chip chip-bad">refused upload</span>}
              {d.checklistItems.length > 0 && (
                <span className="chips chips-inline" aria-label="Checklist items">
                  {d.checklistItems.map((t) => (
                    // The label, not the title: a derived title is long and reads like its
                    // neighbour's, and "2035" is what the correspondence cites (ADR 0007).
                    <span
                      key={t.id}
                      className={`chip chip-tag tag-${t.status.toLowerCase()}`}
                      title={`${t.title} (${checklistStatusLabel(t.status)})`}
                    >
                      {t.docLabel ?? t.title}
                    </span>
                  ))}
                </span>
              )}
            </td>
            {folders && <td className="small muted">{folderName(d.folderId)}</td>}
            <td className="mono">{d.currentVersionNumber ?? '—'}</td>
            <td>{formatBytes(d.sizeBytes)}</td>
            <td className="small muted">{formatUtc(d.currentUploadedUtc)}</td>
            <td className="td-actions">
              {d.permissions.view && d.currentVersionNumber !== null && onPreview && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => onPreview(d.id, d.name)}
                >
                  Preview
                </button>
              )}{' '}
              {d.permissions.download && d.currentVersionNumber !== null && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => void downloadDocument(roomId, d.id)}
                >
                  Download
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DocumentPanel({
  roomId,
  documentId,
  folders,
  roomStatus,
  onClose,
  onPreview,
}: {
  roomId: string
  documentId: string
  folders: FolderResponse[]
  roomStatus: RoomStatus
  onClose: () => void
  onPreview: (version: number, name: string) => void
}) {
  const doc = useDocument(roomId, documentId)
  const rename = useRenameDocument(roomId)
  const remove = useDeleteDocument(roomId)
  const move = useMoveDocument(roomId)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const checklist = useChecklist(roomId)
  const setChecklist = useSetDocumentChecklist(roomId)
  const [tagError, setTagError] = useState<string | null>(null)

  if (doc.isPending) return <p className="muted small">Loading document…</p>
  if (doc.isError)
    return (
      <div className="alert alert-error" role="alert">
        <p>{errorMessage(doc.error)}</p>
      </div>
    )
  const d = doc.data

  return (
    <div className="panel">
      <div className="row-between">
        <h2 className="h2 no-margin">{d.name}</h2>
        <div className="row-actions">
          {d.permissions.editMetadata && (
            <button
              type="button"
              className="btn"
              onClick={() => {
                const name = window.prompt('New document name', d.name)
                if (name && name !== d.name) rename.mutate({ documentId: d.id, name })
              }}
            >
              Rename
            </button>
          )}
          {d.permissions.delete && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (window.confirm(`Delete "${d.name}"?`))
                  remove.mutate(d.id, { onSuccess: onClose })
              }}
            >
              Delete
            </button>
          )}
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <p className="small muted">
        {d.folderIndexNumber ? `${d.folderIndexNumber} ${d.folderName}` : 'Room root'} · added{' '}
        {formatUtc(d.createdUtc)}
        {d.createdBy ? ` by ${d.createdBy}` : ''}
      </p>
      {checklist.data && checklist.data.items.length > 0 && (
        <div className="stack-sm">
          {(d.permissions.editMetadata || d.permissions.upload) &&
          (roomStatus === 'Draft' || roomStatus === 'Open') ? (
            <ChecklistPicker
              items={checklist.data.items}
              sections={checklist.data.sections}
              selected={d.checklistItems.map((t) => t.id)}
              legend="Answers checklist items (the API refuses a change that is not the uploader's or an editor's)"
              onChange={(ids) => {
                setTagError(null)
                setChecklist.mutate(
                  { documentId: d.id, itemIds: ids },
                  { onError: (e) => setTagError(errorMessage(e)) },
                )
              }}
            />
          ) : (
            d.checklistItems.length > 0 && (
              <p className="small muted">
                Answers: {d.checklistItems.map((t) => checklistItemName(t)).join(', ')}
              </p>
            )
          )}
          {tagError && <p className="error-text small">{tagError}</p>}
        </div>
      )}
      {d.permissions.editMetadata && (
        <MoveTo
          folders={folders}
          currentId={d.folderId}
          onMove={(folderId) => move.mutate({ documentId: d.id, folderId })}
          pending={move.isPending}
          error={move.error}
        />
      )}
      {(rename.isError || remove.isError || downloadError) && (
        <p className="error-text small">
          {downloadError ?? errorMessage(rename.error ?? remove.error)}
        </p>
      )}

      <h3 className="h3">Versions</h3>
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Status</th>
            <th>Size</th>
            <th>Uploaded</th>
            <th>By</th>
            <th>Comment</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {d.versions.map((v) => (
            <tr key={v.id}>
              <td className="mono">
                {v.versionNumber}
                {v.isCurrent && <span className="chip">current</span>}
              </td>
              <td>
                <span
                  className={`chip ${v.scanStatus === 'Clean' ? '' : v.scanStatus === 'Pending' ? 'chip-warn' : 'chip-bad'}`}
                >
                  {v.scanStatus}
                </span>
              </td>
              <td>{formatBytes(v.sizeBytes)}</td>
              <td className="small muted">{formatUtc(v.uploadedUtc)}</td>
              <td className="small">{v.uploadedBy ?? v.uploadedByEmail ?? ''}</td>
              <td className="small muted">{v.comment ?? ''}</td>
              <td className="td-actions">
                {d.permissions.view && v.scanStatus === 'Clean' && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => onPreview(v.versionNumber, d.name)}
                  >
                    Preview
                  </button>
                )}{' '}
                {d.permissions.download && v.scanStatus === 'Clean' && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() =>
                      downloadDocument(roomId, d.id, v.versionNumber).catch((e: unknown) =>
                        setDownloadError(errorMessage(e)),
                      )
                    }
                  >
                    Download
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.sha256 && <p className="small muted mono">SHA-256 {d.sha256}</p>}
    </div>
  )
}
